import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, LogOut, User as UserIcon } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useAuth } from "@/components/auth";
import { useBooking } from "@/components/booking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, loading, login, logout } = useAuth();
  const { openBooking } = useBooking();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/my-enquiries`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setEnquiries)
      .catch(() => setEnquiries([]));
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <main className="bg-ivory">
      <SEO title="My Account" description="Your Glam Girl Makeup Studio account — view your appointment enquiries." />
      <section className="grain mx-auto max-w-4xl px-5 pb-24 pt-32 sm:px-8 lg:pt-40" data-testid="account-page">
        <Reveal>
          <Eyebrow>My Account</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-charcoal sm:text-5xl">
            {user ? <>Hello, <em className="text-rose">{user.name?.split(" ")[0]}.</em></> : "Your account"}
          </h1>
        </Reveal>

        {loading ? (
          <p className="mt-12 text-sm text-charcoal/50" data-testid="account-loading">Loading…</p>
        ) : !user ? (
          <Reveal delay={0.1}>
            <div className="mt-12 rounded-2xl border border-charcoal/10 bg-white p-10 text-center" data-testid="account-signin-card">
              <UserIcon className="mx-auto h-8 w-8 text-rose" />
              <h2 className="mt-4 font-serif text-2xl font-medium text-charcoal">Sign in to see your enquiries</h2>
              <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-charcoal/55">
                Use your Google account — booking still works without signing in.
              </p>
              <button
                onClick={login}
                data-testid="account-signin-button"
                className="mt-6 rounded-full bg-charcoal px-8 py-3.5 text-sm font-semibold tracking-wide text-ivory transition-colors hover:bg-rose-deep"
              >
                Sign In with Google
              </button>
            </div>
          </Reveal>
        ) : (
          <>
            <Reveal delay={0.1}>
              <div className="mt-10 flex flex-wrap items-center justify-between gap-5 rounded-2xl border border-charcoal/10 bg-white p-7" data-testid="account-profile-card">
                <div className="flex items-center gap-4">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-charcoal/10 object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-rose font-serif text-xl italic text-ivory">
                      {user.name?.[0]?.toUpperCase()}
                    </span>
                  )}
                  <div>
                    <p className="font-serif text-xl font-medium text-charcoal" data-testid="account-name">{user.name}</p>
                    <p className="text-sm text-charcoal/55" data-testid="account-email">{user.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  data-testid="account-logout-button"
                  className="inline-flex items-center gap-2 rounded-full border border-charcoal/15 px-5 py-2.5 text-xs font-semibold tracking-wide text-charcoal/70 transition-colors hover:border-rose hover:text-rose"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign Out
                </button>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="mt-10" data-testid="account-enquiries">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-2xl font-medium text-charcoal">My enquiries</h2>
                  <button
                    onClick={() => openBooking()}
                    data-testid="account-book-button"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose transition-colors hover:text-rose-deep"
                  >
                    <Calendar className="h-4 w-4" /> New booking
                  </button>
                </div>
                {enquiries === null ? (
                  <p className="mt-6 text-sm text-charcoal/50">Loading…</p>
                ) : enquiries.length === 0 ? (
                  <div className="mt-6 rounded-2xl border border-dashed border-charcoal/15 p-10 text-center" data-testid="account-enquiries-empty">
                    <p className="font-serif text-xl italic text-charcoal/60">No enquiries yet.</p>
                    <p className="mt-2 text-sm text-charcoal/45">Book a service and it will appear here.</p>
                  </div>
                ) : (
                  <ul className="mt-6 space-y-4">
                    {enquiries.map((en) => (
                      <li
                        key={en.id}
                        data-testid={`account-enquiry-${en.id}`}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-charcoal/10 bg-white p-6"
                      >
                        <div>
                          <p className="font-medium text-charcoal">{en.service || "General appointment"}</p>
                          <p className="mt-1 text-xs text-charcoal/50">
                            {[en.date, en.time].filter(Boolean).join(" at ") || "No preferred time"} · Sent{" "}
                            {new Date(en.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          </p>
                        </div>
                        <span className="rounded-full bg-cream px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-charcoal/55">
                          Enquiry sent
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          </>
        )}
      </section>
    </main>
  );
}
